import { civilDay, formatActivityTime, formatTime } from "@/lib/format";
import { ApiError } from "@/lib/http";
import type { DnsChange, DnsRecord } from "@/lib/domains";

/** What the add/edit form holds, kept when going back from the review. */
export interface RecordDraft {
  /** The record being edited; null when adding. */
  record: DnsRecord | null;
  type: string;
  name: string;
  content: string;
  ttl: number;
  priority: string;
}

/** Whether the draft differs from the record it started from (or from a blank one, when adding). */
export function isDraftEdited(draft: RecordDraft): boolean {
  const start = draftFor(draft.record);
  return (["type", "name", "content", "ttl", "priority"] as const).some((k) => draft[k] !== start[k]);
}

export function draftFor(record: DnsRecord | null): RecordDraft {
  return record
    ? {
        record,
        type: record.type,
        name: record.name,
        content: record.content,
        ttl: record.ttl,
        priority: record.priority === null ? "" : String(record.priority),
      }
    : { record: null, type: "A", name: "", content: "", ttl: 3600, priority: "10" };
}

export function usesPriority(type: string): boolean {
  return type === "MX" || type === "SRV";
}

export function changeFor(draft: RecordDraft): DnsChange {
  const priority = usesPriority(draft.type) && draft.priority.trim() !== "" ? Number(draft.priority) : null;
  return {
    op: draft.record ? "update" : "add",
    record_id: draft.record?.id ?? null,
    name: draft.name.trim() || "@",
    type: draft.type,
    content: draft.content.trim(),
    ttl: draft.ttl,
    priority,
  };
}

export const VALUE_PLACEHOLDER: Record<string, string> = {
  A: "76.223.100.4",
  AAAA: "2001:db8::1",
  CNAME: "target.example.com",
  MX: "mail.example.com",
  TXT: "v=spf1 include:_spf.example.com ~all",
  SRV: "weight port target, e.g. 5 5060 sip.example.com",
  CAA: '0 issue "letsencrypt.org"',
};

export const TTL_OPTIONS: { value: number; label: string }[] = [
  { value: 300, label: "5 minutes" },
  { value: 1800, label: "30 minutes" },
  { value: 3600, label: "1 hour" },
  { value: 14400, label: "4 hours" },
  { value: 86400, label: "1 day" },
];

export function ttlLabel(ttl: number): string {
  const known = TTL_OPTIONS.find((o) => o.value === ttl);
  if (known) return known.label;
  if (ttl % 3600 === 0) return `${ttl / 3600} hours`;
  if (ttl % 60 === 0) return `${ttl / 60} minutes`;
  return `${ttl} seconds`;
}

/** "mail.cortexgrip.net (pri 10)" */
export function recordValue(r: Pick<DnsRecord, "content" | "priority" | "type">): string {
  return usesPriority(r.type) && r.priority !== null ? `${r.content} (pri ${r.priority})` : r.content;
}

export function touchesEmail(r: Pick<DnsRecord, "type" | "name" | "content">): boolean {
  const content = r.content.toLowerCase();
  return (
    r.type === "MX" ||
    (r.type === "TXT" && (content.startsWith("v=spf1") || content.startsWith("v=dmarc1"))) ||
    r.name.startsWith("_dmarc") ||
    r.name.includes("._domainkey")
  );
}

export function touchesWebsite(r: Pick<DnsRecord, "type" | "name" | "managed_by">): boolean {
  return (
    r.managed_by === "website" ||
    ((r.name === "@" || r.name === "www") && ["A", "AAAA", "CNAME"].includes(r.type))
  );
}

/** "www.saltandbloom.com" for www, the domain itself for @. */
export function fullHost(name: string, fqdn: string): string {
  return name === "@" ? fqdn : `${name}.${fqdn}`;
}

/** "9:14 am BST" today, "3 Oct, 9:14 am BST" otherwise. */
export function shortTime(iso: string | null): string {
  if (!iso) return "never";
  return civilDay(iso) === civilDay(Date.now()) ? formatTime(iso) : formatActivityTime(iso);
}

/** The message for one form field, if the API put it there. */
export function fieldError(error: unknown, field: string): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  return error.fieldErrors.find((e) => e.field === field || e.field.endsWith(`.${field}`))?.message;
}

export function hasFieldError(error: unknown, fields: string[]): boolean {
  return fields.some((f) => fieldError(error, f) !== undefined);
}
